import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Button,
    Container,
    CircularProgress,
    Pagination,
    Snackbar,
    Slide,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BlockIcon from "@mui/icons-material/Block";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import TuneIcon from "@mui/icons-material/Tune";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import SettingsIcon from "@mui/icons-material/Settings";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import * as XLSX from "xlsx-js-style";
import {
    getFestivalPaymentDetail,
    getFestivalSubmissions,
    getFestivalBankAccount,
    saveFestivalBankAccount,
    deactivateFestivalRouteAccount,
    getFestivalPlatformFee,
    manageSpecificFestivals,
    getFestivalCommission,
    updateFestivalCommission,
    deleteFestivalCommission,
    getPlatformFeeSettings,
    getGlobalCommission,
    issueFestivalPaymentRefund,
} from "../../services/festival";

const BANK_IFSC_MAP = {
    SBIN: "State Bank of India",
    HDFC: "HDFC Bank",
    ICIC: "ICICI Bank",
    UTIB: "Axis Bank",
    KKBK: "Kotak Mahindra Bank",
    PUNB: "Punjab National Bank",
    BARB: "Bank of Baroda",
    CNRB: "Canara Bank",
    UBIN: "Union Bank of India",
    IDFB: "IDFC FIRST Bank",
    YESB: "Yes Bank",
    INDB: "IndusInd Bank",
    FDRL: "Federal Bank",
    IOBA: "Indian Overseas Bank",
    IDIB: "Indian Bank",
    BKID: "Bank of India",
    MAHB: "Bank of Maharashtra",
    PSIB: "Punjab & Sind Bank",
    CBIN: "Central Bank of India",
    UCOB: "UCO Bank",
    SCBL: "Standard Chartered Bank",
    HSBC: "HSBC Bank",
    CITI: "Citibank",
    RATN: "RBL Bank",
    KVBL: "Karur Vysya Bank",
    SIBL: "South Indian Bank",
    TMBL: "Tamilnad Mercantile Bank",
    CSBK: "CSB Bank",
    DCBL: "DCB Bank",
    BDBL: "Bandhan Bank",
    AUBL: "AU Small Finance Bank",
    ESFB: "Equitas Small Finance Bank",
    UJVN: "Ujjivan Small Finance Bank",
    JSFB: "Jana Small Finance Bank",
    PYTM: "Paytm Payments Bank",
    AIRP: "Airtel Payments Bank",
    IPOS: "India Post Payments Bank",
};

// ─── Slide Transition ─────────────────────────────────────────────────────────
const SlideTransition = (props) => <Slide {...props} direction="up" />;

// ─── Date Formatter ───────────────────────────────────────────────────────────
const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

// ─── Reusable Card Component ──────────────────────────────────────────────────
function SectionCard({ title, subtitle, icon, iconBg, iconColor, badge, children }) {
    return (
        <div
            style={{
                background: "#ffffff",
                border: "1px solid #eaecf0",
                borderRadius: "14px",
                padding: "clamp(16px, 3vw, 24px)",
                boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
                marginBottom: "24px",
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px", marginBottom: "20px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0, flex: "1 1 260px" }}>
                    {icon && (
                        <div
                            style={{
                                width: "38px",
                                height: "38px",
                                minWidth: "38px",
                                flexShrink: 0,
                                borderRadius: "10px",
                                background: iconBg || "#f2f4f7",
                                color: iconColor || "#344054",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            {icon}
                        </div>
                    )}
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: "15px", color: "#101828", lineHeight: 1.3 }}>{title}</div>
                        {subtitle && <div style={{ fontSize: "12px", color: "#667085", marginTop: "2px", lineHeight: 1.35 }}>{subtitle}</div>}
                    </div>
                </div>
                {badge && <div style={{ flexShrink: 0 }}>{badge}</div>}
            </div>
            {children}
        </div>
    );
}

export default function FestivalPaymentDetail() {
    const params = useParams();
    const festivalId = params.festivalId || params.id;
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState(0); // 0: Payouts, 1: Settings
    const [loading, setLoading] = useState(true);
    const [bankLoading, setBankLoading] = useState(false);
    const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
    const [deactivateLoading, setDeactivateLoading] = useState(false);
    const [overrideLoading, setOverrideLoading] = useState(false);

    // Festival & Payments Data
    const [festivalData, setFestivalData] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0,
    });

    // Aggregated Financial KPIs
    const [kpis, setKpis] = useState({
        total_submissions: 0,
        gross_revenue: 0,
        platform_fees: 0,
        total_commission: 0,
        freecomers_earnings: 0,
        total_deductions: 0,
        net_settled: 0,
        settled_amount: 0,
        pending_amount: 0,
    });

    // Bank Account Form State
    const [bankForm, setBankForm] = useState({
        beneficiary_name: "",
        email: "",
        account_type: "Current Account (Business/Trust)",
        account_number: "",
        confirm_account_number: "",
        account_number_masked: "",
        ifsc_code: "",
        bank_name: "",
        pan_number: "",
        razorpay_account_id: "",
        verification_status: "pending",
    });
    const [ifscError, setIfscError] = useState("");
    const [ifscLoading, setIfscLoading] = useState(false);

    // Fee Overrides State
    const [globalPlatRate, setGlobalPlatRate] = useState("3.0");
    const [globalCommRate, setGlobalCommRate] = useState("6.0");
    const [platformFeeMode, setPlatformFeeMode] = useState("default");
    const [platformFeeValue, setPlatformFeeValue] = useState("3.0");
    const [commissionMode, setCommissionMode] = useState("default");
    const [commissionValue, setCommissionValue] = useState("6.0");

    // Interactive Column Info Modal State
    const [colModal, setColModal] = useState({
        open: false,
        title: "",
        subtitle: "",
        bodyHtml: "",
        iconColor: "#0284c7",
        iconBg: "#e0f2fe",
    });

    // Toast Snackbar
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    // ─── Refund Modal States ──────────────────────────────────────────────────
    const [refundModalOpen, setRefundModalOpen] = useState(false);
    const [selectedPaymentForRefund, setSelectedPaymentForRefund] = useState(null);
    const [refundReason, setRefundReason] = useState("");
    const [refundLoading, setRefundLoading] = useState(false);

    const handleOpenRefundModal = (payment) => {
        setSelectedPaymentForRefund(payment);
        setRefundReason("");
        setRefundModalOpen(true);
    };

    const handleConfirmRefund = async () => {
        if (!selectedPaymentForRefund) return;
        try {
            setRefundLoading(true);
            const res = await issueFestivalPaymentRefund(selectedPaymentForRefund.id, {
                reason: refundReason || "Admin initiated submission refund",
                amount: selectedPaymentForRefund.net_settled_amount || selectedPaymentForRefund.base_fee_amount,
            });

            if (res?.status) {
                setSnackbar({
                    open: true,
                    message: res.message || "Refund processed successfully with Route reversal.",
                    severity: "success",
                });
                setRefundModalOpen(false);
                setSelectedPaymentForRefund(null);
                fetchPayouts();
            } else {
                setSnackbar({
                    open: true,
                    message: res?.message || "Failed to process refund.",
                    severity: "error",
                });
            }
        } catch (err) {
            console.error("Refund error:", err);
            const errMsg = err.response?.data?.message || "Error processing refund with Razorpay.";
            setSnackbar({
                open: true,
                message: errMsg,
                severity: "error",
            });
        } finally {
            setRefundLoading(false);
        }
    };

    // ─── Column Explanations ──────────────────────────────────────────────────
    const columnExplanations = {
        gross_fee: {
            title: "Gross Fee",
            subtitle: "Total Submission Fee Paid by Filmmaker",
            iconColor: "#0284c7",
            iconBg: "#e0f2fe",
            bodyHtml: "The total fee paid by the filmmaker during checkout before any deductions or splits.",
        },
        freecomers_earnings: {
            title: "Freecomers Earnings",
            subtitle: "Platform Fee (3%) + Freecomers Fee (6%)",
            iconColor: "#0284c7",
            iconBg: "#e0f2fe",
            bodyHtml: "The total platform revenue retained by Freecomers, combining the 3.0% filmmaker checkout platform fee and the 6.0% Freecomers Fee.",
        },
        deductions: {
            title: "Total Deductions",
            subtitle: "Freecomers Fee, Gateway & Taxes",
            iconColor: "#dc2626",
            iconBg: "#fee4e2",
            bodyHtml: "Total amount deducted from the gross submission fee including 6.0% Freecomers Fee, Razorpay Route fee (0.25%), and applicable 18% GST.",
        },
        net_settled: {
            title: "Net Settled to Bank",
            subtitle: "Direct Bank Account Payout",
            iconColor: "#16a34a",
            iconBg: "#dcfce7",
            bodyHtml: "The final net revenue automatically transferred into the festival's verified bank account via Razorpay Route.",
        },
        transfer_id: {
            title: "Transfer ID",
            subtitle: "Razorpay Route Transfer Reference",
            iconColor: "#7c3aed",
            iconBg: "#f3e8ff",
            bodyHtml: "Unique Razorpay Route transfer reference ID generated for this payout split (e.g. trf_TR12345678).",
        },
    };

    const handleOpenColInfo = (key) => {
        const info = columnExplanations[key];
        if (info) {
            setColModal({
                open: true,
                ...info,
            });
        }
    };

    // ─── Fetch Payouts Data ───────────────────────────────────────────────────
    const fetchPayouts = useCallback(async () => {
        if (!festivalId) return;
        try {
            setLoading(true);
            const statusParam = statusFilter !== "all" ? statusFilter : "";
            const [detailRes, subRes] = await Promise.allSettled([
                getFestivalPaymentDetail(festivalId, search, page, statusParam),
                getFestivalSubmissions(festivalId, search, page),
            ]);

            let hasPayments = false;
            if (detailRes.status === "fulfilled" && detailRes.value?.data) {
                const data = detailRes.value.data;
                if (data.festival) {
                    setFestivalData(data.festival);
                }
                if (data.kpis) {
                    const gross = Number(data.kpis.gross_revenue || 0);
                    const plat = Number(data.kpis.platform_fees || 0);
                    const comm = Number(data.kpis.total_commission || 0);
                    const net = Number(data.kpis.net_settled || 0);
                    const deductions = Number(data.kpis.total_deductions || 0);

                    setKpis({
                        total_submissions: Number(data.kpis.total_submissions || 0),
                        gross_revenue: gross,
                        platform_fees: plat,
                        total_commission: comm,
                        freecomers_earnings: Number(data.kpis.freecomers_earnings || 0),
                        total_deductions: deductions,
                        net_settled: net,
                        settled_amount: Number(data.kpis.settled_amount || 0),
                        pending_amount: Number(data.kpis.pending_amount || 0),
                    });
                }
                if (data.bank_account) {
                    const b = data.bank_account;
                    setBankForm(prev => ({
                        ...prev,
                        beneficiary_name: b.beneficiary_name || prev.beneficiary_name,
                        account_type: b.account_type === "savings" ? "Savings Account (Individual)" : "Current Account (Business/Trust)",
                        account_number_masked: b.account_number_masked || prev.account_number_masked,
                        ifsc_code: b.ifsc_code || prev.ifsc_code,
                        bank_name: b.bank_name || prev.bank_name,
                        pan_number: b.pan_number || prev.pan_number,
                        razorpay_account_id: b.razorpay_account_id || prev.razorpay_account_id,
                        verification_status: b.verification_status || prev.verification_status,
                    }));
                }
                if (data.payments?.data && data.payments.data.length > 0) {
                    setSubmissions(data.payments.data);
                    hasPayments = true;
                    setPagination({
                        current_page: data.payments.current_page || 1,
                        last_page: data.payments.last_page || 1,
                        per_page: data.payments.per_page || 20,
                        total: data.payments.total || 0,
                    });
                }
            }

            if (!hasPayments && subRes.status === "fulfilled" && subRes.value?.data) {
                const subData = subRes.value.data;
                if (Array.isArray(subData.submissions)) {
                    setSubmissions(subData.submissions);
                }
            }
        } catch (error) {
            console.error("Error fetching payment detail:", error);
        } finally {
            setLoading(false);
        }
    }, [festivalId, search, page, statusFilter]);

    // ─── Fetch Settings Data (Bank & Overrides) ────────────────────────────────
    const fetchSettings = useCallback(async () => {
        if (!festivalId) return;
        try {
            const [bankRes, platRes, commRes, globalPlatRes, globalCommRes] = await Promise.allSettled([
                getFestivalBankAccount(festivalId),
                getFestivalPlatformFee(festivalId),
                getFestivalCommission(festivalId),
                getPlatformFeeSettings(),
                getGlobalCommission(),
            ]);

            if (bankRes.status === "fulfilled" && bankRes.value?.data) {
                const b = bankRes.value.data;
                setBankForm({
                    beneficiary_name: b.beneficiary_name || "",
                    email: b.email || "",
                    account_type: b.account_type === "savings" ? "Savings Account (Individual)" : "Current Account (Business/Trust)",
                    account_number: "",
                    confirm_account_number: "",
                    account_number_masked: b.account_number_masked || "",
                    ifsc_code: b.ifsc_code || "",
                    bank_name: b.bank_name || "",
                    pan_number: b.pan_number || "",
                    razorpay_account_id: b.razorpay_account_id || "",
                    verification_status: b.verification_status || "pending",
                });
                if (b.ifsc_code) {
                    setIfscError("");
                }
            }

            let activeGlobalPlat = "3.0";
            if (globalPlatRes.status === "fulfilled" && globalPlatRes.value?.data) {
                const gPlat = globalPlatRes.value.data;
                activeGlobalPlat = String(gPlat.value ?? "3.0");
                setGlobalPlatRate(activeGlobalPlat);
            }

            let activeGlobalComm = "6.0";
            if (globalCommRes.status === "fulfilled" && globalCommRes.value?.data) {
                const gComm = globalCommRes.value.data;
                activeGlobalComm = String(gComm.value ?? "6.0");
                setGlobalCommRate(activeGlobalComm);
            }

            if (platRes.status === "fulfilled" && platRes.value?.data) {
                const p = platRes.value.data;
                if (p.is_override) {
                    setPlatformFeeMode(p.type || "percentage");
                    setPlatformFeeValue(String(p.value ?? activeGlobalPlat));
                } else {
                    setPlatformFeeMode("default");
                    setPlatformFeeValue(activeGlobalPlat);
                }
            }

            if (commRes.status === "fulfilled" && commRes.value?.data) {
                const c = commRes.value.data;
                if (c.is_override) {
                    setCommissionMode(c.type || "percentage");
                    setCommissionValue(String(c.value ?? activeGlobalComm));
                } else {
                    setCommissionMode("default");
                    setCommissionValue(activeGlobalComm);
                }
            }
        } catch (error) {
            console.error("Error fetching festival settings:", error);
        }
    }, [festivalId]);

    useEffect(() => {
        if (festivalId) {
            fetchPayouts();
            fetchSettings();
        }
    }, [festivalId, search, page, statusFilter]);

    // ─── Real-time IFSC Code Bank Resolver & Validator ─────────────────────────
    const handleIfscChange = async (val) => {
        const raw = (val || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11);

        setBankForm((prev) => ({
            ...prev,
            ifsc_code: raw,
            bank_name: raw.length === 11 && prev.ifsc_code === raw ? prev.bank_name : "",
        }));

        if (!raw) {
            setIfscError("");
            setIfscLoading(false);
            return;
        }

        // Rule 1: First 4 characters must be alphabetic
        if (raw.length >= 1 && !/^[A-Z]+$/.test(raw.slice(0, Math.min(raw.length, 4)))) {
            setIfscError("First 4 characters must be alphabetic letters (e.g. SBIN)");
            return;
        }

        // Rule 2: 5th character must be '0' (zero)
        if (raw.length >= 5 && raw[4] !== '0') {
            setIfscError("Invalid format: 5th character must be '0' (zero)");
            return;
        }

        // If incomplete (less than 11 characters)
        if (raw.length < 11) {
            setIfscError("");
            setIfscLoading(false);
            return;
        }

        // Rule 3: Full 11 characters format check
        if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(raw)) {
            setIfscError("Invalid IFSC code format (e.g. SBIN0005857)");
            return;
        }

        // Verify with Razorpay public IFSC lookup API
        setIfscLoading(true);
        setIfscError("");
        try {
            const res = await fetch(`https://ifsc.razorpay.com/${raw}`);
            if (res.ok) {
                const data = await res.json();
                if (data && data.BANK) {
                    const fullBank = data.BRANCH ? `${data.BANK} (${data.BRANCH})` : data.BANK;
                    setBankForm((prev) => ({ ...prev, bank_name: fullBank }));
                    setIfscError("");
                } else {
                    const prefix = raw.slice(0, 4);
                    setBankForm((prev) => ({ ...prev, bank_name: BANK_IFSC_MAP[prefix] || "Verified Bank" }));
                    setIfscError("");
                }
            } else {
                setBankForm((prev) => ({ ...prev, bank_name: "" }));
                setIfscError("Invalid IFSC code: Bank branch not found");
            }
        } catch (err) {
            const prefix = raw.slice(0, 4);
            if (BANK_IFSC_MAP[prefix]) {
                setBankForm((prev) => ({ ...prev, bank_name: BANK_IFSC_MAP[prefix] }));
                setIfscError("");
            } else {
                setIfscError("Invalid IFSC code: Bank branch not found");
            }
        } finally {
            setIfscLoading(false);
        }
    };

    const handleIfscBlur = () => {
        const raw = (bankForm.ifsc_code || "").trim().toUpperCase();
        if (!raw) return;
        if (raw.length < 11) {
            setIfscError(`Invalid IFSC code: Must be exactly 11 characters (entered ${raw.length})`);
            return;
        }
        if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(raw)) {
            setIfscError("Invalid IFSC code format (e.g. SBIN0005857)");
            return;
        }
    };

    // ─── Save Bank Account ────────────────────────────────────────────────────
    const handleSaveBank = async (e) => {
        e.preventDefault();
        const ifsc = (bankForm.ifsc_code || "").trim().toUpperCase();
        if (!ifsc || ifsc.length !== 11 || !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) {
            const err = "Invalid IFSC code format (must be 11 characters, e.g. SBIN0005857).";
            setIfscError(err);
            setSnackbar({ open: true, message: err, severity: "error" });
            return;
        }
        if (ifscError) {
            setSnackbar({ open: true, message: ifscError, severity: "error" });
            return;
        }
        if (bankForm.account_number && bankForm.account_number.trim().length > 0) {
            const accNum = bankForm.account_number.trim();
            if (!/^[0-9]+$/.test(accNum)) {
                setSnackbar({ open: true, message: "Bank account number must contain digits only.", severity: "error" });
                return;
            }
            if (accNum.length < 9 || accNum.length > 18) {
                setSnackbar({ open: true, message: `Bank account number must be between 9 and 18 digits (entered ${accNum.length}).`, severity: "error" });
                return;
            }
            if (!bankForm.confirm_account_number || bankForm.confirm_account_number.trim().length === 0) {
                setSnackbar({ open: true, message: "Please confirm your bank account number.", severity: "error" });
                return;
            }
            if (accNum !== bankForm.confirm_account_number.trim()) {
                setSnackbar({ open: true, message: "Bank account numbers do not match.", severity: "error" });
                return;
            }
        }
        try {
            setBankLoading(true);
            const payload = {
                beneficiary_name: bankForm.beneficiary_name,
                email: bankForm.email,
                account_type: bankForm.account_type?.toLowerCase().includes("saving") ? "savings" : "current",
                account_number: bankForm.account_number,
                ifsc_code: bankForm.ifsc_code,
                pan_number: bankForm.pan_number,
            };
            const res = await saveFestivalBankAccount(festivalId, payload);
            setSnackbar({
                open: true,
                message: res?.message || "Bank details verified and saved successfully! Razorpay Route linked.",
                severity: "success",
            });
            fetchSettings();
        } catch (error) {
            console.error("Save bank error:", error);
            const msg = error?.response?.data?.message || "Failed to save bank account.";
            setSnackbar({ open: true, message: msg, severity: "error" });
        } finally {
            setBankLoading(false);
        }
    };

    // ─── Deactivate & Suspend Linked Account ────────────────────────────────
    const handleDeactivateAccount = async () => {
        try {
            setDeactivateLoading(true);
            const res = await deactivateFestivalRouteAccount(festivalId);
            setSnackbar({
                open: true,
                message: res?.message || "Linked Razorpay Route account deactivated & suspended successfully! You can now add and link a new account.",
                severity: "success",
            });
            setDeactivateModalOpen(false);
            await fetchSettings();
        } catch (error) {
            console.error("Deactivate account error:", error);
            const msg = error?.response?.data?.message || "Failed to deactivate and suspend account.";
            setSnackbar({ open: true, message: msg, severity: "error" });
        } finally {
            setDeactivateLoading(false);
        }
    };

    // ─── Save Fee Overrides ───────────────────────────────────────────────────
    const handleSaveOverrides = async () => {
        try {
            setOverrideLoading(true);
            // 1. Platform Fee Override
            if (platformFeeMode !== "default") {
                await manageSpecificFestivals({
                    festival_id: Number(festivalId),
                    action: "save",
                    type: platformFeeMode === "exempt" ? "percentage" : platformFeeMode,
                    value: platformFeeMode === "exempt" ? 0 : Number(platformFeeValue),
                    enabled: platformFeeMode !== "exempt",
                });
            } else {
                try {
                    await manageSpecificFestivals({
                        festival_id: Number(festivalId),
                        action: "delete",
                    });
                } catch (delErr) {
                    // Ignore if no override existed
                }
            }

            // 2. Festival Commission Override
            if (commissionMode !== "default") {
                await updateFestivalCommission(festivalId, {
                    type: commissionMode,
                    value: Number(commissionValue),
                    enabled: true,
                });
            } else {
                try {
                    await deleteFestivalCommission(festivalId);
                } catch (delErr) {
                    // Ignore if no override existed
                }
            }

            setSnackbar({ open: true, message: "Festival fee overrides updated successfully!", severity: "success" });
            fetchSettings();
        } catch (error) {
            console.error("Save fee overrides error:", error);
            const msg = error?.response?.data?.message || "Failed to update fee overrides.";
            setSnackbar({ open: true, message: msg, severity: "error" });
        } finally {
            setOverrideLoading(false);
        }
    };

    // ─── Export to Excel / CSV ────────────────────────────────────────────────
    const handleExportExcel = () => {
        if (!submissions || submissions.length === 0) {
            alert("No submission records available to export.");
            return;
        }
        const rows = submissions.map(s => ({
            "Tracking ID": s.tracking_id || s.transfer_id || "N/A",
            "Film Title": s.film_title || s.project_title || "N/A",
            "Submitter": s.filmmaker_name || s.submitter || "N/A",
            "Category": s.category_name || s.category || "General",
            "Gross Fee (₹)": Number(s.base_fee_amount || s.gross_amount || 0).toFixed(2),
            "Freecomers Earnings (₹)": (s.settlement_status === "settled" && s.freecomers_earnings != null)
                ? Number(s.freecomers_earnings).toFixed(2)
                : "--",
            "Deductions (₹)": Number(s.total_deductions || s.commission_amount || 0).toFixed(2),
            "Net Settled (₹)": Number(s.net_settled_amount || 0).toFixed(2),
            "Transfer ID": s.transfer_id || s.razorpay_transfer_id || "N/A",
            "Status": s.settlement_status || "SETTLED",
            "Date": formatDate(s.created_at || s.date),
        }));

        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Payouts");
        XLSX.writeFile(wb, `payouts_ledger_festival_${festivalId}_${new Date().toISOString().split("T")[0]}.xlsx`);
    };

    // ─── Field and Typography Styles ──────────────────────────────────────────
    const inputStyle = {
        width: "100%",
        padding: "10px 42px 10px 14px",
        border: "1px solid #d0d5dd",
        borderRadius: "8px",
        fontSize: "14px",
        fontWeight: 600,
        color: "#101828",
        background: "#ffffff",
        outline: "none",
        boxSizing: "border-box",
        fontFamily: "inherit",
    };

    const disabledInputStyle = {
        ...inputStyle,
        background: "#f2f4f7",
        color: "#475467",
        cursor: "not-allowed",
    };

    const labelStyle = {
        fontSize: "12px",
        fontWeight: 600,
        color: "#344054",
        marginBottom: "6px",
        display: "block",
    };

    const helperStyle = {
        fontSize: "11px",
        color: "#667085",
        marginTop: "4px",
    };

    const isBankVerified = bankForm.verification_status === "verified";
    const totalEarnings = Number(kpis.freecomers_earnings || 0);

    return (
        <Container maxWidth="xl" sx={{ py: 3, mb: 10 }}>
            {/* CSS to remove number input spinners */}
            <style>{`
                input[type=number]::-webkit-inner-spin-button,
                input[type=number]::-webkit-outer-spin-button {
                    -webkit-appearance: none !important;
                    margin: 0 !important;
                }
                input[type=number] {
                    -moz-appearance: textfield !important;
                }
            `}</style>

            {/* ── Top Back Button ── */}
            <div style={{ marginBottom: "16px" }}>
                <button
                    type="button"
                    onClick={() => navigate("/festivals/payments")}
                    style={{
                        background: "#ffffff",
                        border: "1px solid #d0d5dd",
                        borderRadius: "8px",
                        padding: "7px 14px",
                        fontWeight: 600,
                        fontSize: "13px",
                        color: "#344054",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        transition: "all 0.2s",
                    }}
                >
                    <ArrowBackIcon sx={{ fontSize: 16 }} /> Back to All Festivals
                </button>
            </div>

            {/* ── Festival Header Title Row ── */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: "16px",
                    marginBottom: "6px",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                    <h1 style={{ fontSize: "24px", fontWeight: 800, color: "#101828", margin: 0 }}>
                        {festivalData?.name || `Festival #${festivalId}`}
                    </h1>
                    {isBankVerified ? (
                        <span
                            style={{
                                background: "#ecfdf5",
                                color: "#027a48",
                                border: "1px solid #a7f3d0",
                                borderRadius: "6px",
                                padding: "4px 10px",
                                fontSize: "12px",
                                fontWeight: 700,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "5px",
                            }}
                        >
                            <AccountBalanceIcon sx={{ fontSize: 14 }} /> Verified {bankForm.bank_name || "SBI"} ({bankForm.account_number_masked || "••••"})
                        </span>
                    ) : bankForm.verification_status === "suspended" ? (
                        <span
                            style={{
                                background: "#fff1f2",
                                color: "#e11d48",
                                border: "1px solid #fecdd3",
                                borderRadius: "6px",
                                padding: "4px 10px",
                                fontSize: "12px",
                                fontWeight: 700,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "5px",
                            }}
                        >
                            <BlockIcon sx={{ fontSize: 14 }} /> Account Suspended & Deactivated
                        </span>
                    ) : (
                        <span
                            style={{
                                background: "#fef3c7",
                                color: "#b45309",
                                border: "1px solid #fde68a",
                                borderRadius: "6px",
                                padding: "4px 10px",
                                fontSize: "12px",
                                fontWeight: 700,
                            }}
                        >
                            ⚠️ Bank Pending
                        </span>
                    )}
                </div>

                <button
                    type="button"
                    onClick={handleExportExcel}
                    style={{
                        background: "#ffffff",
                        border: "1px solid #d0d5dd",
                        borderRadius: "8px",
                        padding: "8px 16px",
                        fontWeight: 600,
                        fontSize: "13px",
                        color: "#344054",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        transition: "all 0.2s",
                        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
                    }}
                >
                    <DownloadIcon sx={{ fontSize: 16 }} /> Export Ledger CSV
                </button>
            </div>

            {/* ── Metadata Subtitle ── */}
            <div style={{ fontSize: "13px", color: "#667085", marginBottom: "24px" }}>
                Organiser: <strong style={{ color: "#344054" }}>{festivalData?.organiser || bankForm.beneficiary_name || "Festival Organiser"}</strong> •{" "}
                IFSC: <strong style={{ color: "#344054" }}>{bankForm.ifsc_code || "N/A"}</strong> •{" "}
                Razorpay Account ID: <strong style={{ color: "#344054" }}>{bankForm.razorpay_account_id || "acc_pending_link"}</strong> •{" "}
                <span style={{ color: "#0284c7", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: "4px" }}>
                    <AccessTimeIcon sx={{ fontSize: 14 }} /> T+7 Settlement Cycle
                </span>
            </div>

            {/* ── Red Underline Navigation Tabs ── */}
            <div style={{ display: "flex", gap: "24px", borderBottom: "1px solid #eaecf0", marginBottom: "24px" }}>
                <button
                    type="button"
                    onClick={() => setActiveTab(0)}
                    style={{
                        background: "none",
                        border: "none",
                        borderBottom: activeTab === 0 ? "3px solid #e90000" : "3px solid transparent",
                        padding: "10px 4px",
                        fontSize: "14px",
                        fontWeight: activeTab === 0 ? 800 : 600,
                        color: activeTab === 0 ? "#e90000" : "#667085",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                    }}
                >
                    <CreditCardIcon sx={{ fontSize: 18 }} /> Payouts
                </button>
                <button
                    type="button"
                    onClick={() => setActiveTab(1)}
                    style={{
                        background: "none",
                        border: "none",
                        borderBottom: activeTab === 1 ? "3px solid #e90000" : "3px solid transparent",
                        padding: "10px 4px",
                        fontSize: "14px",
                        fontWeight: activeTab === 1 ? 800 : 600,
                        color: activeTab === 1 ? "#e90000" : "#667085",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                    }}
                >
                    <SettingsIcon sx={{ fontSize: 18 }} /> Settings
                </button>
            </div>

            {/* ══════════════════════════════════════════════════════════════════════
                TAB 1: PAYOUTS LEDGER VIEW (MATCHING MOCKUP 1:1)
               ══════════════════════════════════════════════════════════════════════ */}
            {activeTab === 0 && (
                <div>
                    {/* ── 4 KPI Summary Stat Cards ── */}
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                            gap: "16px",
                            marginBottom: "24px",
                        }}
                    >
                        {/* Card 1: Gross Paid by Filmmakers */}
                        <div
                            style={{
                                background: "#ffffff",
                                border: "1px solid #eaecf0",
                                borderRadius: "14px",
                                padding: "18px 20px",
                                boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
                            }}
                        >
                            <div style={{ fontSize: "11px", color: "#667085", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.5px" }}>
                                Gross Paid by Filmmakers
                            </div>
                            <div style={{ fontSize: "22px", fontWeight: 800, color: "#101828", margin: "4px 0" }}>
                                ₹{Number(kpis.gross_revenue || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                            </div>
                            <div style={{ fontSize: "11px", color: "#667085" }}>
                                {kpis.total_submissions} Film Submissions (Paid)
                            </div>
                        </div>

                        {/* Card 2: Freecomers Earnings (9%) */}
                        <div
                            style={{
                                background: "#ffffff",
                                border: "1px solid #eaecf0",
                                borderRadius: "14px",
                                padding: "18px 20px",
                                boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
                            }}
                        >
                            <div style={{ fontSize: "11px", color: "#0284c7", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.5px" }}>
                                Freecomers Earnings ({Number(globalPlatRate) + Number(globalCommRate)}%)
                            </div>
                            <div style={{ fontSize: "22px", fontWeight: 800, color: "#0284c7", margin: "4px 0" }}>
                                ₹{Number(totalEarnings || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                            </div>
                            <div style={{ fontSize: "11px", color: "#667085" }}>
                                Realized earnings from settled submissions
                            </div>
                        </div>

                        {/* Card 3: Total Deductions */}
                        <div
                            style={{
                                background: "#ffffff",
                                border: "1px solid #eaecf0",
                                borderRadius: "14px",
                                padding: "18px 20px",
                                boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
                            }}
                        >
                            <div style={{ fontSize: "11px", color: "#dc2626", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.5px" }}>
                                Total Deductions
                            </div>
                            <div style={{ fontSize: "22px", fontWeight: 800, color: "#dc2626", margin: "4px 0" }}>
                                -₹{Number(kpis.total_deductions || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                            </div>
                            <div style={{ fontSize: "11px", color: "#667085" }}>
                                Freecomers Fee, Gateway & Taxes
                            </div>
                        </div>

                        {/* Card 4: Net Settled to Bank */}
                        <div
                            style={{
                                background: "#ffffff",
                                border: "1px solid #eaecf0",
                                borderRadius: "14px",
                                padding: "18px 20px",
                                boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
                            }}
                        >
                            <div style={{ fontSize: "11px", color: "#7c3aed", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.5px" }}>
                                Net Settled to Bank
                            </div>
                            <div style={{ fontSize: "22px", fontWeight: 800, color: "#7c3aed", margin: "4px 0" }}>
                                ₹{Number(kpis.net_settled || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                            </div>
                            <div style={{ fontSize: "11px", color: "#667085" }}>
                                {bankForm.bank_name || "Federal Bank"} {bankForm.account_number_masked || "••••"} • T+7 Cycle{Number(kpis.pending_amount || 0) > 0 ? ` (₹${Number(kpis.pending_amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })} Pending)` : ""}
                            </div>
                        </div>
                    </div>

                    {/* ── Payouts Ledger Table ── */}
                    <div
                        style={{
                            background: "#ffffff",
                            border: "1px solid #eaecf0",
                            borderRadius: "14px",
                            overflow: "hidden",
                            boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
                        }}
                    >
                        <div style={{ overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "13px" }}>
                                <thead>
                                    <tr style={{ background: "#f8fafc", borderBottom: "1px solid #eaecf0" }}>
                                        <th style={{ padding: "12px 18px", fontSize: "12px", fontWeight: 700, color: "#475467" }}>Film Title</th>
                                        <th style={{ padding: "12px 18px", fontSize: "12px", fontWeight: 700, color: "#475467" }}>Submitter</th>
                                        <th style={{ padding: "12px 18px", fontSize: "12px", fontWeight: 700, color: "#475467" }}>Category</th>
                                        <th style={{ padding: "12px 18px", fontSize: "12px", fontWeight: 700, color: "#475467" }}>
                                            Gross Fee{" "}
                                            <span
                                                onClick={() => handleOpenColInfo("gross_fee")}
                                                style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    width: "14px",
                                                    height: "14px",
                                                    borderRadius: "50%",
                                                    background: "#e2e8f0",
                                                    color: "#475467",
                                                    fontSize: "9px",
                                                    cursor: "pointer",
                                                    marginLeft: "4px",
                                                }}
                                            >
                                                i
                                            </span>
                                        </th>
                                        <th style={{ padding: "12px 18px", fontSize: "12px", fontWeight: 700, color: "#475467" }}>
                                            Freecomers Earnings{" "}
                                            <span
                                                onClick={() => handleOpenColInfo("freecomers_earnings")}
                                                style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    width: "14px",
                                                    height: "14px",
                                                    borderRadius: "50%",
                                                    background: "#e2e8f0",
                                                    color: "#475467",
                                                    fontSize: "9px",
                                                    cursor: "pointer",
                                                    marginLeft: "4px",
                                                }}
                                            >
                                                i
                                            </span>
                                        </th>
                                        <th style={{ padding: "12px 18px", fontSize: "12px", fontWeight: 700, color: "#475467" }}>
                                            Deductions{" "}
                                            <span
                                                onClick={() => handleOpenColInfo("deductions")}
                                                style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    width: "14px",
                                                    height: "14px",
                                                    borderRadius: "50%",
                                                    background: "#e2e8f0",
                                                    color: "#475467",
                                                    fontSize: "9px",
                                                    cursor: "pointer",
                                                    marginLeft: "4px",
                                                }}
                                            >
                                                i
                                            </span>
                                        </th>
                                        <th style={{ padding: "12px 18px", fontSize: "12px", fontWeight: 700, color: "#475467" }}>
                                            Net Settled{" "}
                                            <span
                                                onClick={() => handleOpenColInfo("net_settled")}
                                                style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    width: "14px",
                                                    height: "14px",
                                                    borderRadius: "50%",
                                                    background: "#e2e8f0",
                                                    color: "#475467",
                                                    fontSize: "9px",
                                                    cursor: "pointer",
                                                    marginLeft: "4px",
                                                }}
                                            >
                                                i
                                            </span>
                                        </th>
                                        <th style={{ padding: "12px 18px", fontSize: "12px", fontWeight: 700, color: "#475467" }}>
                                            Transfer ID{" "}
                                            <span
                                                onClick={() => handleOpenColInfo("transfer_id")}
                                                style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    width: "14px",
                                                    height: "14px",
                                                    borderRadius: "50%",
                                                    background: "#e2e8f0",
                                                    color: "#475467",
                                                    fontSize: "9px",
                                                    cursor: "pointer",
                                                    marginLeft: "4px",
                                                }}
                                            >
                                                i
                                            </span>
                                        </th>
                                        <th style={{ padding: "12px 18px", fontSize: "12px", fontWeight: 700, color: "#475467" }}>Status</th>
                                        <th style={{ padding: "12px 18px", fontSize: "12px", fontWeight: 700, color: "#475467", textAlign: "right" }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={10} style={{ padding: "40px", textAlign: "center" }}>
                                                <CircularProgress size={30} sx={{ color: "#e90000" }} />
                                            </td>
                                        </tr>
                                    ) : submissions.length === 0 ? (
                                        <tr>
                                            <td colSpan={10} style={{ padding: "40px", textAlign: "center", color: "#667085" }}>
                                                No submission payments recorded yet.
                                            </td>
                                        </tr>
                                    ) : (
                                        submissions.map((sub, idx) => {
                                            const grossFee = Number(sub.base_fee_amount || sub.amount || sub.gross_amount || 0);
                                            const platFee = Number(sub.platform_fee_amount || (grossFee * 0.03));
                                            const commFee = Number(sub.commission_amount || (grossFee * 0.06));
                                            const totalEarn = platFee + commFee;
                                            const deductions = Number(sub.total_deductions || (commFee * 1.18));
                                            const netSettled = Number(sub.net_settled_amount || (grossFee - deductions));
                                            const isSettled = (sub.settlement_status || sub.payment_status || "").toLowerCase() !== "pending";

                                            return (
                                                <tr
                                                    key={sub.id || sub.submission_id || idx}
                                                    style={{ borderBottom: "1px solid #f2f4f7", transition: "background-color 0.15s" }}
                                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#faf8f5")}
                                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                                                >
                                                    {/* Film Title */}
                                                    <td style={{ padding: "14px 18px", fontWeight: 700, color: "#101828" }}>
                                                        {sub.film_title || sub.project_title || "Untitled Film"}
                                                    </td>

                                                    {/* Submitter */}
                                                    <td style={{ padding: "14px 18px", color: "#344054" }}>
                                                        {sub.filmmaker_name || sub.submitter || "Filmmaker"}
                                                    </td>

                                                    {/* Category */}
                                                    <td style={{ padding: "14px 18px", color: "#475467" }}>
                                                        {sub.category_name || sub.category || "General Entry"}
                                                    </td>

                                                    {/* Gross Fee */}
                                                    <td style={{ padding: "14px 18px", fontWeight: 700, color: "#101828" }}>
                                                        ₹{grossFee.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                                    </td>

                                                    {/* Freecomers Earnings */}
                                                    <td style={{ padding: "14px 18px" }}>
                                                        {sub.settlement_status === "settled" && sub.freecomers_earnings != null ? (
                                                            <div style={{ fontWeight: 700, color: "#0284c7" }}>
                                                                ₹{Number(sub.freecomers_earnings).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                                            </div>
                                                        ) : (
                                                            <div style={{ fontWeight: 600, color: "#98a2b3" }}>
                                                                --
                                                            </div>
                                                        )}
                                                    </td>

                                                    {/* Deductions */}
                                                    <td style={{ padding: "14px 18px", fontWeight: 600, color: "#dc2626" }}>
                                                        -₹{deductions.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                                    </td>

                                                    {/* Net Settled */}
                                                    <td style={{ padding: "14px 18px", fontWeight: 700, color: "#16a34a" }}>
                                                        ₹{netSettled.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                                                    </td>

                                                    {/* Transfer ID */}
                                                    <td style={{ padding: "14px 18px", fontFamily: "monospace", fontSize: "12px", color: "#475467" }}>
                                                        {sub.transfer_id || sub.razorpay_transfer_id || "trf_pending"}
                                                    </td>

                                                    {/* Status Badge */}
                                                    <td style={{ padding: "14px 18px" }}>
                                                        {sub.settlement_status === "refunded" ? (
                                                            <span
                                                                style={{
                                                                    background: "#f3e8ff",
                                                                    color: "#7c3aed",
                                                                    border: "1px solid #d8b4fe",
                                                                    padding: "2px 8px",
                                                                    borderRadius: "6px",
                                                                    fontSize: "11px",
                                                                    fontWeight: 700,
                                                                    display: "inline-flex",
                                                                    alignItems: "center",
                                                                    gap: "4px",
                                                                }}
                                                                title={sub.refund_id ? `Refund ID: ${sub.refund_id}` : "Refunded"}
                                                            >
                                                                REFUNDED
                                                            </span>
                                                        ) : isSettled ? (
                                                            <span
                                                                style={{
                                                                    background: "#ecfdf5",
                                                                    color: "#027a48",
                                                                    border: "1px solid #a7f3d0",
                                                                    padding: "2px 8px",
                                                                    borderRadius: "6px",
                                                                    fontSize: "11px",
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                SETTLED
                                                            </span>
                                                        ) : (
                                                            <span
                                                                style={{
                                                                    background: "#fef3c7",
                                                                    color: "#b45309",
                                                                    border: "1px solid #fde68a",
                                                                    padding: "2px 8px",
                                                                    borderRadius: "6px",
                                                                    fontSize: "11px",
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                PENDING
                                                            </span>
                                                        )}
                                                    </td>

                                                    {/* Actions */}
                                                    <td style={{ padding: "14px 18px", textAlign: "right" }}>
                                                        {sub.settlement_status === "refunded" ? (
                                                            <span style={{ fontSize: "11px", color: "#9ca3af", fontStyle: "italic", fontFamily: "monospace" }}>
                                                                {sub.refund_id ? sub.refund_id : "Refunded"}
                                                            </span>
                                                        ) : (sub.razorpay_payment_id || sub.transaction_id) ? (
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={() => handleOpenRefundModal(sub)}
                                                                startIcon={<CurrencyExchangeIcon sx={{ fontSize: "14px !important" }} />}
                                                                sx={{
                                                                    textTransform: "none",
                                                                    fontSize: "11.5px",
                                                                    fontWeight: 700,
                                                                    borderColor: "#fecdd3",
                                                                    color: "#e11d48",
                                                                    bgcolor: "#fff1f2",
                                                                    px: 1.5,
                                                                    py: 0.3,
                                                                    borderRadius: "6px",
                                                                    "&:hover": {
                                                                        bgcolor: "#ffe4e6",
                                                                        borderColor: "#fda4af",
                                                                    },
                                                                }}
                                                            >
                                                                Refund
                                                            </Button>
                                                        ) : (
                                                            <span style={{ fontSize: "12px", color: "#9ca3af" }}>—</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination Footer */}
                        {pagination.last_page > 1 && (
                            <div
                                style={{
                                    padding: "14px 20px",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    borderTop: "1px solid #eaecf0",
                                    background: "#fcfcfd",
                                }}
                            >
                                <div style={{ fontSize: "12px", color: "#667085" }}>
                                    Showing page <strong>{pagination.current_page}</strong> of <strong>{pagination.last_page}</strong> ({pagination.total} total payouts)
                                </div>
                                <Pagination
                                    count={pagination.last_page}
                                    page={pagination.current_page}
                                    onChange={(e, v) => setPage(v)}
                                    shape="rounded"
                                    sx={{
                                        "& .Mui-selected": {
                                            bgcolor: "#e90000 !important",
                                            color: "#fff !important",
                                        },
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════════════════════════════════
                TAB 2: SETTINGS (BANK ONBOARDING & FEE OVERRIDES)
               ══════════════════════════════════════════════════════════════════════ */}
            {activeTab === 1 && (
                <div>
                    {/* ── CARD 1: BANK ACCOUNT & RAZORPAY ROUTE ONBOARDING ── */}
                    <SectionCard
                        title="Festival Bank Account & Razorpay Route Onboarding"
                        subtitle="Add or update festival settlement bank details to link with Razorpay Route (Direct Split Payouts)"
                        icon={<AccountBalanceIcon sx={{ fontSize: 20 }} />}
                        iconBg="#ecfdf5"
                        iconColor="#027a48"
                        badge={
                            isBankVerified ? (
                                <span
                                    style={{
                                        background: "#ecfdf5",
                                        color: "#027a48",
                                        border: "1px solid #a7f3d0",
                                        padding: "4px 10px",
                                        borderRadius: "6px",
                                        fontSize: "12px",
                                        fontWeight: 700,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "4px",
                                    }}
                                >
                                    <CheckCircleIcon sx={{ fontSize: 15 }} /> ROUTE LINKED: {bankForm.razorpay_account_id}
                                </span>
                            ) : bankForm.verification_status === "suspended" ? (
                                <span
                                    style={{
                                        background: "#fff1f2",
                                        color: "#e11d48",
                                        border: "1px solid #fecdd3",
                                        padding: "4px 10px",
                                        borderRadius: "6px",
                                        fontSize: "12px",
                                        fontWeight: 700,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "4px",
                                    }}
                                >
                                    <BlockIcon sx={{ fontSize: 15 }} /> ACCOUNT SUSPENDED & DEACTIVATED
                                </span>
                            ) : (
                                <span
                                    style={{
                                        background: "#fffbeb",
                                        color: "#b45309",
                                        border: "1px solid #fde68a",
                                        padding: "4px 10px",
                                        borderRadius: "6px",
                                        fontSize: "12px",
                                        fontWeight: 700,
                                    }}
                                >
                                    ROUTE UNLINKED
                                </span>
                            )
                        }
                    >
                        <form onSubmit={handleSaveBank}>
                            {isBankVerified ? (
                                <div
                                    style={{
                                        background: "#eff6ff",
                                        border: "1px solid #bfdbfe",
                                        borderRadius: "8px",
                                        padding: "12px 16px",
                                        marginBottom: "20px",
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: "12px",
                                    }}
                                >
                                    <InfoIcon sx={{ color: "#2563eb", fontSize: 20, mt: 0.2, flexShrink: 0 }} />
                                    <div style={{ fontSize: "13px", color: "#1e3a8a", lineHeight: 1.5 }}>
                                        <strong>Active Razorpay Route Account Linked ({bankForm.razorpay_account_id}):</strong> Bank details are locked to safeguard automated split payouts. If you need to change or replace the festival's settlement bank account, you must <strong>first deactivate and suspend</strong> this previous account before adding a new one.
                                    </div>
                                </div>
                            ) : bankForm.verification_status === "suspended" ? (
                                <div
                                    style={{
                                        background: "#fff1f2",
                                        border: "1px solid #fecdd3",
                                        borderRadius: "8px",
                                        padding: "12px 16px",
                                        marginBottom: "20px",
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: "12px",
                                    }}
                                >
                                    <WarningAmberIcon sx={{ color: "#e11d48", fontSize: 20, mt: 0.2, flexShrink: 0 }} />
                                    <div style={{ fontSize: "13px", color: "#881337", lineHeight: 1.5 }}>
                                        <strong>Previous Account Deactivated & Suspended:</strong> The previous linked account has been suspended on Razorpay Route. You can now enter fresh bank account details below and click <strong>"Save & Verify Bank Account"</strong> to onboard the new account.
                                    </div>
                                </div>
                            ) : null}
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px", marginBottom: "20px" }}>
                                <div>
                                    <label style={labelStyle}>
                                        Beneficiary Account Name <span style={{ color: "#ef4444" }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={bankForm.beneficiary_name}
                                        onChange={(e) => setBankForm({ ...bankForm, beneficiary_name: e.target.value })}
                                        required
                                        disabled={isBankVerified}
                                        style={isBankVerified ? disabledInputStyle : inputStyle}
                                    />
                                    <div style={helperStyle}>Must match the registered entity or bank passbook name exactly</div>
                                </div>
                                <div>
                                    <label style={labelStyle}>
                                        Organizer Email Address <span style={{ color: "#ef4444" }}>*</span>
                                    </label>
                                    <input
                                        type="email"
                                        value={bankForm.email}
                                        onChange={(e) => setBankForm({ ...bankForm, email: e.target.value })}
                                        placeholder="organizer@example.com"
                                        required
                                        disabled={isBankVerified}
                                        style={isBankVerified ? disabledInputStyle : inputStyle}
                                    />
                                    <div style={helperStyle}>Linked to Razorpay Route for organizer settlement notifications</div>
                                </div>
                                <div>
                                    <label style={labelStyle}>
                                        Account Type <span style={{ color: "#ef4444" }}>*</span>
                                    </label>
                                    <select
                                        value={bankForm.account_type}
                                        onChange={(e) => setBankForm({ ...bankForm, account_type: e.target.value })}
                                        disabled={isBankVerified}
                                        style={{ ...(isBankVerified ? disabledInputStyle : inputStyle), cursor: isBankVerified ? "not-allowed" : "pointer" }}
                                    >
                                        <option value="Current Account (Business/Trust)">Current Account (Business/Trust)</option>
                                        <option value="Savings Account (Individual)">Savings Account (Individual)</option>
                                    </select>
                                </div>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px", marginBottom: "20px" }}>
                                <div>
                                    <label style={labelStyle}>
                                        Bank Account Number <span style={{ color: "#ef4444" }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder={bankForm.account_number_masked || "Enter account number"}
                                        value={isBankVerified ? (bankForm.account_number_masked || "••••••••") : bankForm.account_number}
                                        maxLength={18}
                                        disabled={isBankVerified}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 18);
                                            setBankForm((prev) => ({
                                                ...prev,
                                                account_number: val,
                                                confirm_account_number: val ? prev.confirm_account_number : "",
                                            }));
                                        }}
                                        style={{
                                            ...(isBankVerified ? disabledInputStyle : inputStyle),
                                            borderColor: !isBankVerified && bankForm.account_number && bankForm.account_number.length > 0
                                                ? (bankForm.account_number.length < 9 ? "#f59e0b" : "#16a34a")
                                                : (inputStyle.border ? inputStyle.border.replace("1px solid ", "") : "#d0d5dd"),
                                        }}
                                    />
                                    {bankForm.account_number && bankForm.account_number.length > 0 ? (
                                        bankForm.account_number.length < 9 ? (
                                            <div style={{ ...helperStyle, color: "#d97706", fontWeight: 600, display: "flex", alignItems: "center", gap: "4px" }}>
                                                <ErrorOutlineIcon sx={{ fontSize: 14, color: "#d97706" }} /> Must be between 9 and 18 digits ({bankForm.account_number.length}/18 entered)
                                            </div>
                                        ) : (
                                            <div style={{ ...helperStyle, color: "#16a34a", fontWeight: 700, display: "flex", alignItems: "center", gap: "4px" }}>
                                                <CheckCircleIcon sx={{ fontSize: 14, color: "#16a34a" }} /> Valid account number length ({bankForm.account_number.length} digits)
                                            </div>
                                        )
                                    ) : (
                                        <div style={helperStyle}>Must be between 9 and 18 digits (numbers only)</div>
                                    )}
                                </div>
                                {!isBankVerified && Boolean(bankForm.account_number && bankForm.account_number.trim().length > 0) && (
                                    <div>
                                        <label style={labelStyle}>
                                            Confirm Bank Account Number <span style={{ color: "#ef4444" }}>*</span>
                                        </label>
                                        <input
                                            type="password"
                                            placeholder="Re-enter account number"
                                            maxLength={18}
                                            value={bankForm.confirm_account_number}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 18);
                                                setBankForm((prev) => ({ ...prev, confirm_account_number: val }));
                                            }}
                                            required={true}
                                            style={{
                                                ...inputStyle,
                                                borderColor: bankForm.confirm_account_number && bankForm.confirm_account_number.length > 0
                                                    ? (bankForm.confirm_account_number === bankForm.account_number ? "#16a34a" : "#ef4444")
                                                    : (inputStyle.border ? inputStyle.border.replace("1px solid ", "") : "#d0d5dd"),
                                            }}
                                        />
                                        {bankForm.confirm_account_number && bankForm.confirm_account_number.length > 0 && (
                                            bankForm.confirm_account_number !== bankForm.account_number ? (
                                                <div style={{ ...helperStyle, color: "#ef4444", fontWeight: 700, display: "flex", alignItems: "center", gap: "4px" }}>
                                                    <ErrorOutlineIcon sx={{ fontSize: 14, color: "#ef4444" }} /> Account numbers do not match
                                                </div>
                                            ) : (
                                                <div style={{ ...helperStyle, color: "#16a34a", fontWeight: 700, display: "flex", alignItems: "center", gap: "4px" }}>
                                                    <CheckCircleIcon sx={{ fontSize: 14, color: "#16a34a" }} /> Account numbers match
                                                </div>
                                            )
                                        )}
                                    </div>
                                )}
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px", marginBottom: "24px" }}>
                                <div>
                                    <label style={labelStyle}>
                                        IFSC Code <span style={{ color: "#ef4444" }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g. SBIN0005857"
                                        maxLength={11}
                                        value={bankForm.ifsc_code}
                                        onChange={(e) => handleIfscChange(e.target.value)}
                                        onBlur={handleIfscBlur}
                                        required
                                        disabled={isBankVerified}
                                        style={{
                                            ...(isBankVerified ? disabledInputStyle : inputStyle),
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px",
                                            borderColor: !isBankVerified && ifscError ? "#ef4444" : (!isBankVerified && bankForm.bank_name ? "#16a34a" : (inputStyle.border ? inputStyle.border.replace("1px solid ", "") : "#d0d5dd")),
                                            background: isBankVerified ? (disabledInputStyle.background || "#f8fafc") : (ifscError ? "#fff5f5" : (inputStyle.background || "#ffffff")),
                                        }}
                                    />
                                    {ifscLoading ? (
                                        <div style={{ ...helperStyle, color: "#0284c7", display: "flex", alignItems: "center", gap: "5px", marginTop: "4px" }}>
                                            <span style={{ display: "inline-block", width: "10px", height: "10px", borderRadius: "50%", border: "2px solid #0284c7", borderTopColor: "transparent", animation: "spin 1s linear infinite" }}></span> Verifying IFSC details...
                                        </div>
                                    ) : ifscError ? (
                                        <div style={{ ...helperStyle, color: "#ef4444", fontWeight: 700, display: "flex", alignItems: "center", gap: "4px", marginTop: "4px" }}>
                                            <ErrorOutlineIcon sx={{ fontSize: 16, color: "#ef4444" }} /> {ifscError}
                                        </div>
                                    ) : bankForm.bank_name ? (
                                        <div style={{ ...helperStyle, color: "#16a34a", fontWeight: 700, display: "flex", alignItems: "center", gap: "4px", marginTop: "4px" }}>
                                            <CheckCircleIcon sx={{ fontSize: 15, color: "#16a34a" }} /> {bankForm.bank_name}
                                        </div>
                                    ) : bankForm.ifsc_code && bankForm.ifsc_code.length > 0 && bankForm.ifsc_code.length < 11 ? (
                                        <div style={{ ...helperStyle, color: "#667085", marginTop: "4px" }}>
                                            {11 - bankForm.ifsc_code.length} more characters needed (e.g. SBIN0005857)
                                        </div>
                                    ) : null}
                                </div>
                                <div>
                                    <label style={labelStyle}>
                                        PAN Number (Business / Organizer) <span style={{ color: "#ef4444" }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={bankForm.pan_number}
                                        onChange={(e) => setBankForm({ ...bankForm, pan_number: e.target.value.toUpperCase() })}
                                        required
                                        disabled={isBankVerified}
                                        style={{ ...(isBankVerified ? disabledInputStyle : inputStyle), textTransform: "uppercase" }}
                                    />
                                </div>
                                <div>
                                    <label style={labelStyle}>Razorpay Route Account ID</label>
                                    <input
                                        type="text"
                                        value={bankForm.razorpay_account_id || (bankForm.verification_status === "suspended" ? "Deactivated (Ready for new account)" : "acc_pending_link")}
                                        disabled
                                        style={disabledInputStyle}
                                    />
                                    <div style={helperStyle}>Auto-assigned upon verification via Razorpay API</div>
                                </div>
                            </div>

                            <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                                {isBankVerified ? (
                                    <Button
                                        type="button"
                                        variant="contained"
                                        onClick={() => setDeactivateModalOpen(true)}
                                        disabled={deactivateLoading}
                                        startIcon={deactivateLoading ? <CircularProgress size={16} color="inherit" /> : <BlockIcon />}
                                        sx={{
                                            textTransform: "none",
                                            borderRadius: 2,
                                            px: 3,
                                            py: 1.2,
                                            fontWeight: 700,
                                            fontSize: "13px",
                                            bgcolor: "#dc2626",
                                            color: "#ffffff",
                                            width: { xs: "100%", sm: "auto" },
                                            "&:hover": { bgcolor: "#b91c1c" },
                                        }}
                                    >
                                        {deactivateLoading ? "Deactivating & Suspending..." : "Deactivate & Suspend Account"}
                                    </Button>
                                ) : (
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={bankLoading}
                                        startIcon={bankLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                                        sx={{
                                            textTransform: "none",
                                            borderRadius: 2,
                                            px: 3,
                                            py: 1.2,
                                            fontWeight: 700,
                                            fontSize: "13px",
                                            bgcolor: "#e90000",
                                            width: { xs: "100%", sm: "auto" },
                                            "&:hover": { bgcolor: "#c80000" },
                                        }}
                                    >
                                        {bankLoading ? "Saving Bank..." : "Save & Verify Bank Account"}
                                    </Button>
                                )}
                            </div>
                        </form>
                    </SectionCard>

                    {/* ── CARD 2: FESTIVAL PLATFORM FEE & FREECOMERS FEE OVERRIDES ── */}
                    <SectionCard
                        title="Festival Platform Fee & Freecomers Fee Overrides"
                        subtitle="Override the global platform fee (3.0%) and Freecomers fee (6.0%) specifically for this festival"
                        icon={<TuneIcon sx={{ fontSize: 20 }} />}
                        iconBg="#eff8ff"
                        iconColor="#175cd3"
                    >
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px" }}>
                            {/* Platform Fee Override Box */}
                            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                                    <label style={{ fontWeight: 700, fontSize: "14px", color: "#101828", margin: 0 }}>Platform Fee (Filmmaker Side)</label>
                                    <span style={{ background: "#eff8ff", color: "#175cd3", border: "1px solid #b2ddff", fontSize: "11px", fontWeight: 700, padding: "3px 8px", borderRadius: "6px" }}>
                                        Global: {globalPlatRate}%
                                    </span>
                                </div>

                                <div style={{ marginBottom: "12px" }}>
                                    <label style={{ ...labelStyle, fontSize: "12px", marginBottom: "6px" }}>Fee Mode</label>
                                    <select
                                        value={platformFeeMode}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setPlatformFeeMode(val);
                                            if (val === "default") {
                                                setPlatformFeeValue(globalPlatRate);
                                            } else if (val === "exempt") {
                                                setPlatformFeeValue("0");
                                            }
                                        }}
                                        style={{
                                            ...inputStyle,
                                            fontSize: "13px",
                                            cursor: "pointer",
                                            borderColor: platformFeeMode !== "default" ? "#e90000" : "#d0d5dd",
                                        }}
                                    >
                                        <option value="default">Use Global Default ({globalPlatRate}%)</option>
                                        <option value="percentage">Custom Percentage (%)</option>
                                        <option value="fixed">Custom Fixed Amount (₹)</option>
                                        <option value="exempt">Exempt (0% - No Platform Fee)</option>
                                    </select>
                                </div>

                                <div>
                                    <label style={{ ...labelStyle, fontSize: "12px", marginBottom: "6px" }}>Custom Value</label>
                                    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                                        <input
                                            type="number"
                                            value={platformFeeMode === "default" ? globalPlatRate : (platformFeeMode === "exempt" ? "0" : platformFeeValue)}
                                            onChange={(e) => setPlatformFeeValue(e.target.value)}
                                            disabled={platformFeeMode === "default" || platformFeeMode === "exempt"}
                                            style={platformFeeMode === "default" || platformFeeMode === "exempt" ? disabledInputStyle : inputStyle}
                                        />
                                        <span style={{ position: "absolute", right: "14px", fontSize: "13px", color: "#667085", fontWeight: 600 }}>
                                            {platformFeeMode === "fixed" ? "₹" : "%"}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Freecomers Fee Override Box */}
                            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                                    <label style={{ fontWeight: 700, fontSize: "14px", color: "#101828", margin: 0 }}>Freecomers Fee (Payout Cut)</label>
                                    <span style={{ background: "#ecfdf5", color: "#027a48", border: "1px solid #a7f3d0", fontSize: "11px", fontWeight: 700, padding: "3px 8px", borderRadius: "6px" }}>
                                        Global: {globalCommRate}%
                                    </span>
                                </div>

                                <div style={{ marginBottom: "12px" }}>
                                    <label style={{ ...labelStyle, fontSize: "12px", marginBottom: "6px" }}>Freecomers Fee Mode</label>
                                    <select
                                        value={commissionMode}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setCommissionMode(val);
                                            if (val === "default") {
                                                setCommissionValue(globalCommRate);
                                            }
                                        }}
                                        style={{
                                            ...inputStyle,
                                            fontSize: "13px",
                                            cursor: "pointer",
                                            borderColor: commissionMode !== "default" ? "#e90000" : "#d0d5dd",
                                        }}
                                    >
                                        <option value="default">Use Global Default ({globalCommRate}%)</option>
                                        <option value="percentage">Custom Percentage (%)</option>
                                        <option value="fixed">Custom Fixed Amount (₹)</option>
                                    </select>
                                </div>

                                <div>
                                    <label style={{ ...labelStyle, fontSize: "12px", marginBottom: "6px" }}>Custom Value</label>
                                    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                                        <input
                                            type="number"
                                            value={commissionMode === "default" ? globalCommRate : commissionValue}
                                            onChange={(e) => setCommissionValue(e.target.value)}
                                            disabled={commissionMode === "default"}
                                            style={commissionMode === "default" ? disabledInputStyle : inputStyle}
                                        />
                                        <span style={{ position: "absolute", right: "14px", fontSize: "13px", color: "#667085", fontWeight: 600 }}>
                                            {commissionMode === "fixed" ? "₹" : "%"}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer Action Buttons */}
                        <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap", borderTop: "1px solid #f2f4f7", paddingTop: "20px", marginTop: "20px" }}>
                            <Button
                                type="button"
                                variant="contained"
                                startIcon={overrideLoading ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                                disabled={overrideLoading}
                                onClick={handleSaveOverrides}
                                sx={{
                                    textTransform: "none",
                                    borderRadius: 2,
                                    px: 3,
                                    py: 1.2,
                                    fontWeight: 700,
                                    fontSize: "13px",
                                    bgcolor: "#e90000",
                                    width: { xs: "100%", sm: "auto" },
                                    "&:hover": { bgcolor: "#c80000" },
                                }}
                            >
                                {overrideLoading ? "Saving Overrides..." : "Save Festival Fee Overrides"}
                            </Button>

                            <Button
                                type="button"
                                variant="outlined"
                                onClick={() => {
                                    setPlatformFeeMode("default");
                                    setPlatformFeeValue(globalPlatRate);
                                    setCommissionMode("default");
                                    setCommissionValue(globalCommRate);
                                }}
                                sx={{
                                    textTransform: "none",
                                    borderRadius: 2,
                                    px: 2.5,
                                    py: 1.2,
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    borderColor: "#d0d5dd",
                                    color: "#344054",
                                    width: { xs: "100%", sm: "auto" },
                                    "&:hover": { borderColor: "#98a2b3", bgcolor: "#f9fafb" },
                                }}
                            >
                                Reset to Global Defaults
                            </Button>
                        </div>
                    </SectionCard>
                </div>
            )}

            {/* ── Interactive Column Explanation Modal ── */}
            <Dialog
                open={colModal.open}
                onClose={() => setColModal(prev => ({ ...prev, open: false }))}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        p: 1,
                        maxWidth: 460,
                        width: "100%",
                    },
                }}
            >
                <DialogTitle sx={{ pb: 1, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div
                            style={{
                                width: "40px",
                                height: "40px",
                                borderRadius: "10px",
                                background: colModal.iconBg,
                                color: colModal.iconColor,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "18px",
                            }}
                        >
                            <InfoIcon sx={{ fontSize: 22 }} />
                        </div>
                        <div>
                            <Typography variant="subtitle1" fontWeight={800} color="#101828" sx={{ m: 0 }}>
                                {colModal.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {colModal.subtitle}
                            </Typography>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => setColModal(prev => ({ ...prev, open: false }))}
                        style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#98a2b3",
                            padding: "4px",
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </button>
                </DialogTitle>

                <DialogContent sx={{ pt: 2, pb: 3 }}>
                    <Typography variant="body2" color="#475467" sx={{ lineHeight: 1.6 }}>
                        {colModal.bodyHtml}
                    </Typography>
                </DialogContent>
            </Dialog>

            {/* ── Modal: Confirm Deactivate & Suspend Linked Account ── */}
            <Dialog
                open={deactivateModalOpen}
                onClose={() => !deactivateLoading && setDeactivateModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        p: 1,
                        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                    },
                }}
            >
                <DialogTitle sx={{ pb: 1, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <div
                            style={{
                                width: "40px",
                                height: "40px",
                                borderRadius: "10px",
                                background: "#fee2e2",
                                color: "#dc2626",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <BlockIcon sx={{ fontSize: 22 }} />
                        </div>
                        <div>
                            <Typography sx={{ fontWeight: 800, fontSize: "17px", color: "#111827" }}>
                                Deactivate & Suspend Linked Account?
                            </Typography>
                            <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                                Razorpay Route ID: <code style={{ fontWeight: 700, color: "#dc2626" }}>{bankForm.razorpay_account_id}</code>
                            </Typography>
                        </div>
                    </div>
                    <Button
                        onClick={() => setDeactivateModalOpen(false)}
                        disabled={deactivateLoading}
                        sx={{ minWidth: 0, p: 0.5, color: "#9ca3af", "&:hover": { color: "#4b5563" } }}
                    >
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </Button>
                </DialogTitle>

                <DialogContent sx={{ pt: 1.5, pb: 2 }}>
                    <div
                        style={{
                            background: "#fff5f5",
                            border: "1px solid #fed7d7",
                            borderRadius: "8px",
                            padding: "14px",
                            marginBottom: "16px",
                            fontSize: "13px",
                            color: "#9b2c2c",
                            lineHeight: 1.6,
                        }}
                    >
                        <strong>Warning:</strong> Are you sure you want to deactivate and suspend this linked account?
                        <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
                            <li>The account will be <strong>suspended via the Razorpay API</strong>.</li>
                            <li>Automated Route split payouts to this account will be <strong>immediately halted</strong>.</li>
                            <li>Existing bank details will be unlinked, allowing you to enter and link a <strong>new bank account</strong> for this festival.</li>
                        </ul>
                    </div>
                    <div style={{ fontSize: "13px", color: "#4b5563", background: "#f8fafc", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                        <div>Festival: <strong>{festivalData?.name || `Festival #${festivalId}`}</strong></div>
                        <div>Current Bank: <strong>{bankForm.bank_name || "Bank"} ({bankForm.account_number_masked})</strong></div>
                        <div>Beneficiary: <strong>{bankForm.beneficiary_name}</strong></div>
                    </div>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2, pt: 0, gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setDeactivateModalOpen(false)}
                        disabled={deactivateLoading}
                        sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            px: 2.5,
                            fontWeight: 600,
                            borderColor: "#d1d5db",
                            color: "#374151",
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDeactivateAccount}
                        disabled={deactivateLoading}
                        startIcon={deactivateLoading ? <CircularProgress size={16} color="inherit" /> : <BlockIcon />}
                        sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            px: 3,
                            fontWeight: 700,
                            bgcolor: "#dc2626",
                            color: "#ffffff",
                            "&:hover": { bgcolor: "#b91c1c" },
                        }}
                    >
                        {deactivateLoading ? "Suspending Account..." : "Yes, Deactivate & Suspend"}
                    </Button>
                </DialogActions>
            </Dialog>

                        {/* ── Modal: Issue Submission Refund ── */}
            <Dialog
                open={refundModalOpen}
                onClose={() => !refundLoading && setRefundModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        p: 1,
                        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                    },
                }}
            >
                <DialogTitle sx={{ pb: 1, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <div
                            style={{
                                width: "42px",
                                height: "42px",
                                borderRadius: "10px",
                                background: "#fff1f2",
                                color: "#e11d48",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <CurrencyExchangeIcon sx={{ fontSize: 24 }} />
                        </div>
                        <div>
                            <Typography sx={{ fontWeight: 800, fontSize: "17px", color: "#111827" }}>
                                Issue Submission Refund
                            </Typography>
                            <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                                Razorpay Payment ID: <code style={{ fontWeight: 700, color: "#e11d48" }}>{selectedPaymentForRefund?.razorpay_payment_id || selectedPaymentForRefund?.transfer_id || "N/A"}</code>
                            </Typography>
                        </div>
                    </div>
                    <Button
                        onClick={() => setRefundModalOpen(false)}
                        disabled={refundLoading}
                        sx={{ minWidth: 0, p: 0.5, color: "#9ca3af", "&:hover": { color: "#4b5563" } }}
                    >
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </Button>
                </DialogTitle>

                <DialogContent sx={{ pt: 1.5, pb: 2 }}>
                    {/* Reversal notice */}
                    <div
                        style={{
                            background: "#fff7ed",
                            border: "1px solid #fed7aa",
                            borderRadius: "10px",
                            padding: "14px",
                            marginBottom: "16px",
                            fontSize: "13px",
                            color: "#9a3412",
                            lineHeight: 1.6,
                        }}
                    >
                        <strong>Filmmaker Refund Policy & Route Reversal:</strong>
                        <ul style={{ margin: "6px 0 0 0", paddingLeft: "18px" }}>
                            <li>
                                <strong>Amount Refunded to Filmmaker:</strong> Only the net festival payout (
                                <strong>₹{Number(selectedPaymentForRefund?.net_settled_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong>
                                ) will be refunded to the filmmaker.
                            </li>
                            <li>
                                <strong>Excluded Platform & Processing Fees:</strong> Platform Fee (3%), Gateway Fee (3% + 18% GST), Freecomers Fee (6%), and Route Fees are retained.
                            </li>
                            <li>
                                <strong>Organizer Route Payout:</strong> Exactly 
                                <strong> ₹{Number(selectedPaymentForRefund?.net_settled_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong>
                                is reversed from the organizer's linked Route account to fund this refund (₹0 out of pocket for Freecomers).
                            </li>
                        </ul>
                    </div>

                    {/* Breakdown Info */}
                    <div style={{ fontSize: "13px", color: "#374151", background: "#f8fafc", padding: "14px", borderRadius: "10px", border: "1px solid #e2e8f0", marginBottom: "16px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                            <span style={{ color: "#6b7280" }}>Film Title:</span>
                            <strong>{selectedPaymentForRefund?.film_title || "Untitled Film"}</strong>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                            <span style={{ color: "#6b7280" }}>Filmmaker:</span>
                            <span><strong>{selectedPaymentForRefund?.filmmaker_name || "Filmmaker"}</strong> ({selectedPaymentForRefund?.filmmaker_email || "N/A"})</span>
                        </div>
                        <div style={{ borderTop: "1px dashed #cbd5e1", marginTop: "10px", paddingTop: "10px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                                <span style={{ color: "#6b7280" }}>Total Gross Paid by Filmmaker:</span>
                                <span style={{ fontWeight: 600, color: "#111827" }}>₹{Number(selectedPaymentForRefund?.gross_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", fontSize: "12px", color: "#b91c1c" }}>
                                <span>- Retained Platform Fee (3%):</span>
                                <span>-₹{Number(selectedPaymentForRefund?.platform_fee_amount || (Number(selectedPaymentForRefund?.base_fee_amount || 0) * 0.03)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", fontSize: "12px", color: "#b91c1c" }}>
                                <span>- Retained Gateway Fee (3%) + GST (18%):</span>
                                <span>-₹{Number((Number(selectedPaymentForRefund?.gateway_fee_amount || 0) + Number(selectedPaymentForRefund?.gateway_gst_amount || 0)) || 36.46).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", fontSize: "12px", color: "#b91c1c" }}>
                                <span>- Retained Freecomers Fee (6%) & Route Fee:</span>
                                <span>-₹{Number((Number(selectedPaymentForRefund?.commission_amount || 0) + Number(selectedPaymentForRefund?.route_fee_amount || 0) + Number(selectedPaymentForRefund?.route_gst_amount || 0)) || 62.77).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "8px", borderTop: "1px solid #e2e8f0" }}>
                                <span style={{ fontWeight: 700, color: "#047857" }}>Net Refund to Filmmaker:</span>
                                <span style={{ fontWeight: 800, fontSize: "15px", color: "#047857" }}>₹{Number(selectedPaymentForRefund?.net_settled_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "6px", fontSize: "12px", color: "#6b7280" }}>
                                <span>Organizer Payout Reversal:</span>
                                <span style={{ fontFamily: "monospace", color: "#374151", fontWeight: 600 }}>₹{Number(selectedPaymentForRefund?.net_settled_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                            </div>
                        </div>
                    </div>

                    {/* Reason input */}
                    <TextField
                        fullWidth
                        label="Refund Reason / Notes"
                        placeholder="e.g. Submitter requested cancellation, Duplicate payment, Category withdrawn"
                        value={refundReason}
                        onChange={(e) => setRefundReason(e.target.value)}
                        variant="outlined"
                        size="small"
                        multiline
                        rows={2}
                        disabled={refundLoading}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                            },
                        }}
                    />
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2, pt: 0, gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setRefundModalOpen(false)}
                        disabled={refundLoading}
                        sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            px: 2.5,
                            fontWeight: 600,
                            borderColor: "#d1d5db",
                            color: "#374151",
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmRefund}
                        disabled={refundLoading}
                        startIcon={refundLoading ? <CircularProgress size={16} color="inherit" /> : <CurrencyExchangeIcon />}
                        sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            px: 3,
                            fontWeight: 700,
                            bgcolor: "#e11d48",
                            color: "#ffffff",
                            "&:hover": { bgcolor: "#be123c" },
                        }}
                    >
                        {refundLoading ? "Processing Refund..." : `Confirm Refund (₹${Number(selectedPaymentForRefund?.net_settled_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })})`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Snackbar Toast ── */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                TransitionComponent={SlideTransition}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%", borderRadius: 2, fontWeight: 600 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}
